import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { BackendService } from 'src/app/backend.service'
import { StudentClass, UserRole } from 'src/app/model/user-model'
import { GroupMemberCollection } from 'src/app/my-students/my-students-model'
import { DirectusFiles, DirectusRoles, DirectusUsers, Group, Organisation } from 'src/types'

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _user$ = new BehaviorSubject<DirectusUsers | any>(null)
  user$ = this._user$.asObservable()
  private _avatarsList$ = new BehaviorSubject<Array<DirectusFiles | string | null>>([])
  avatarsList$ = this._avatarsList$.asObservable()
  private _defaultAvatar$ = new BehaviorSubject<Partial<DirectusFiles> | string | null>(null)
  defaultAvatar$ = this._defaultAvatar$.asObservable()
  private _userOrg$ = new BehaviorSubject<Organisation | null | undefined>(null)
  userOrg$ = this._userOrg$.asObservable()
  private _userOrgs$ = new BehaviorSubject<Organisation[]>([])
  userOrgs$ = this._userOrgs$.asObservable()
  role: string = ''

  constructor(private backendService: BackendService) {
    this.backendService.token$.subscribe((t) => {
      if (t) this.extractUserData()
    })
  }

  resetSvc() {
    this._user$.next(null)
    this._avatarsList$.next([])
    this._defaultAvatar$.next(null)
    this._userOrg$.next(undefined)
    this._userOrgs$.next([])
    this.role = ''
  }

  async extractUserData() {
    const user = (await this.backendService.getLoggedUser()) as DirectusUsers
    this.role = (user?.role as Partial<DirectusRoles>)?.name ?? ''
    this.setUserOrgs(user)
    await this.setAvatar(user as DirectusUsers)
  }

  setUserOrgs(user: DirectusUsers) {
    const orgs: Organisation[] = user.organisations.map((org) => org.organisation_id)
    const providerOrgs = orgs.filter((org) => org.isLanguageProvider)
    const defaultProviderOrg = (user.default_organisation as Organisation) ?? providerOrgs[0]

    // No school/org selection for sub schools, set the first one is school/org, if it exists
    const defaultSubSchoolOrg = orgs.filter((org) => !org.isLanguageProvider)[0]
    const subSchoolOrgs = defaultSubSchoolOrg ? [defaultSubSchoolOrg] : []

    // Ideally, a user can't be in both provider org and sub school org, but set provider org higher priority just in case
    if (providerOrgs.length) {
      this._userOrgs$.next(providerOrgs)
      this.setUserOrg(defaultProviderOrg ?? null)
    } else {
      this._userOrgs$.next(subSchoolOrgs)
      this.setUserOrg(defaultSubSchoolOrg ?? null)
    }
  }

  setUserOrg(org: Organisation | null) {
    this._userOrg$.next(org)
  }

  async setAvatar(user: DirectusUsers) {
    if (!user) return
    const avatarData: Array<Partial<DirectusFiles>> | string | null | undefined =
      await this.backendService.getFilesByFolderNames(['game_avatars'])
    this._avatarsList$.next(avatarData as any)

    const defaultAvatar: Partial<DirectusFiles> | string | null | undefined = avatarData?.find(
      (avatar) => avatar?.title === 'Word Turtle Sea'
    )
    if (defaultAvatar) this._defaultAvatar$.next(defaultAvatar)

    if (!user.avatar) user.avatar = defaultAvatar as any
    this._user$.next(user)
  }

  async getUser() {
    if (!this._user$.getValue()) await this.extractUserData()
    return this._user$.getValue()
  }

  async updateUser(user: Partial<DirectusUsers>) {
    await this.backendService.updateLoggedUser(user)
    const updatedUser = { ...this._user$.getValue(), ...user }
    this._user$.next(updatedUser)
  }

  async getStudentClasses() {
    if (!this._user$.getValue()) await this.getUser()
    if (this.role === UserRole.Teacher) return []

    const id = this._user$.getValue().id
    const currentClasses = (await this.backendService.getMemberGroups(GroupMemberCollection.Member, id))
      .filter((g) => g.group_id)
      .map((c) => ({
        id: (c.group_id as Group).id as string,
        name: (c.group_id as Group).name as string,
        isPending: false,
        orgId: ((c.group_id as Group).organisation as Organisation)?.id
      }))

    const pendingClasses = (await this.backendService.getMemberGroups(GroupMemberCollection.PendingMember, id))
      .filter((g) => g.group_id)
      .map((c) => ({
        id: (c.group_id as Group).id as string,
        name: (c.group_id as Group).name as string,
        isPending: true,
        orgId: ((c.group_id as Group).organisation as Organisation)?.id
      }))

    const studentClasses: StudentClass[] = [...currentClasses, ...pendingClasses]
    return studentClasses
  }

  async acceptClassInvitation(classId: string) {
    const studentId = await this.getUserId()
    const member = {
      group_id: classId,
      directus_users_id: studentId
    }
    await this.backendService.addGroupMember(GroupMemberCollection.Member, member)
  }

  async rejectClassInvitation(classId: string) {
    const studentId = await this.getUserId()
    const studentsPendingClass = (
      await this.backendService.getMemberGroups(GroupMemberCollection.PendingMember, studentId)
    ).find((g) => g.group_id?.id === classId)

    const currentUser = await this.getUser()
    if (!studentsPendingClass || !currentUser) return

    await this.backendService.updateGroupPendingMember(studentsPendingClass.id, {
      rejectedBy: currentUser.id,
      groupName: studentsPendingClass.group_id.name,
      notifyUser: studentsPendingClass.notifyUser
    })
    await this.backendService.deleteGroupPendingMembers(classId, [studentId])
  }

  async removeClassInvitation(classId: string) {
    const studentId = await this.getUserId()
    await this.backendService.deleteGroupPendingMembers(classId, [studentId])
  }

  async getUserId() {
    if (!this._user$.getValue()) await this.getUser()
    return this._user$.getValue().id
  }

  navigationUrl(role: UserRole | string | null = this.role) {
    switch (role) {
      case UserRole.Teacher:
        return '/teacher'
      case UserRole.ContentManager:
        return '/content-management'
      case UserRole.UserAdmin:
        return '/content-management/users-admin'
      default:
        return '/student'
    }
  }
}
